import { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Container from '@/components/ui/Container'
import Lines from '@/components/ui/Lines'
import Logo from '@/components/ui/Logo'
import { resolveNav, type ResolvedNavItem, type ResolvedNavNode, type NavLocale } from '@/cms/nav'
import { NAV_MAX_DEPTH } from '@/content/nav-types'
import { safeExternalUrl } from '@/utils/safeExternalUrl'
import { buildLocalePath, type Locale } from '@/utils/localePath'

/** Map an i18next language onto the locales the CMS stores labels for. */
function toNavLocale(language: string): NavLocale {
  if (language.startsWith('zh')) {
    return /Hans|zh-CN|zh-SG/i.test(language) ? 'zh-Hans' : 'zh-Hant'
  }
  return 'en'
}

// Native language names so users who don't read English can still find their language.
const LANGUAGES = [
  { code: 'zh-Hant', flag: '🇭🇰', short: '繁中', name: '繁體中文' },
  { code: 'zh-Hans', flag: '🇨🇳', short: '简中', name: '简体中文' },
  { code: 'en', flag: '🇬🇧', short: 'EN', name: 'English' },
] as const

export default function Navbar() {
  const { t, i18n } = useTranslation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const langRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  // The menu comes from the CMS (cmsNav, generated by npm run cms:sync) and
  // falls back to the compiled-in default list when the CMS has no visible
  // items — see resolveNav for why that fallback matters.
  const items = resolveNav(toNavLocale(i18n.language), t)

  // Detect scroll for sticky shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false)
    setLangOpen(false)
  }, [location.pathname])

  // Close language dropdown when clicking outside
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Close menu on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false)
        setLangOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  const currentLang =
    LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0]
  const isZh = i18n.language.startsWith('zh')

  return (
    <header
      className={`sticky top-0 z-40 bg-surface border-b border-border transition-shadow ${
        scrolled ? 'shadow-yada' : ''
      }`}
    >
      <Container>
        {/* Phase 1 mobile (2026-10-13): row height was 80px mobile / 110px
            desktop, with a 70px-tall logo. The 80px chrome ate the first
            scroll on every phone page. Compact mobile to 64px (h-16) and
            the logo to <Logo size="sm"> (56 → 64 px on md+). Desktop
            chrome (110px row) now gates at xl with the inline nav —
            sticky-header-offset follows that same 1280px cut.

            2026-10-14 follow-up chain: 40 px made the 2.86:1 lockup read
            as a 114 px-wide sliver on a 414 px screen. Bumped sm's mobile
            step first to 48 px, then to 56 px after feedback that 48 px
            was still too small. The 56 px logo in the 64 px row leaves
            4 px padding top/bottom — tight but acceptable on phones
            where the brand mark needs to be the first thing users see.

            2026-10-14 (later): desktop step bumped again, 64 → 80 px on
            Logo `sm`, because the wide 2.86:1 lockup looked anemic
            against the 110 px chrome. 80 px + 110 px = 30 px of headroom,
            matches the brand wall's `lg` step. The row is still 110 px
            tall, so the sticky-header-offset token (128 px md+) doesn't
            need to follow.

            2026-10-18: mobile row 64 → 80 px (h-20), matching the 2026-10-13
            height this file had abandoned for eating first scroll. The
            difference is that the logo is now a dedicated step rather than
            the `sm` step's mobile arm: `sm` is shared with other surfaces,
            so raising it would have dragged every `sm` consumer along.
            `navbar` gives this row its own value and leaves Footer (`md`) /
            About (`lg`) / Admin (`xs`) untouched.

            The 76 px logo in this 88 px row restores the visual breathing
            room the design had before 2026-10-17. Note the CSS box padding
            (6 px) is NOT the apparent gap: the logo PNG carries 15.5% baked
            -in vertical padding, so the visible gap is ~17.8 px. See
            Logo.tsx for the measurement.

            This DOES move `--sticky-header-offset` 5rem → 6.5rem in
            globals.css — 6 files consume it for `#anchor` scroll padding,
            and leaving it lower would park every anchor target under the
            header. Keep the two in step.

            2026-10-20 (responsive band): row height used to jump to 110px
            at `md` (768px) while the primary nav stays `hidden` until
            `xl` (1280px). Shrinking a laptop into that band kept desktop
            chrome (110px row + 80px logo + inline CTA) with only the
            hamburger swapped in — it read as a scaled-down desktop, not
            a responsive layout. Desktop chrome now lands at the same
            `xl` gate as the inline nav; below that the mobile 88px row
            (and drawer CTA) stays in force. */}
        <div className="flex items-center justify-between h-[88px] xl:h-[110px]">
          {/* Logo via shared <Logo> component. `eager` because navbar
              is the first paint. `size="navbar"` is the navbar-only step
              (76 px below xl → 80 px xl+) — see Logo.tsx for why the row
              needs its own size rather than borrowing `sm`. */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" aria-label="YADA Home">
            <Logo variant="horizontal" size="navbar" eager />
          </Link>

          {/* Desktop nav — xl (≥1280px) so EN labels don't crowd the logo/CTA on laptops */}
          <nav className="hidden xl:flex items-center gap-5" aria-label="Primary">
            {items.map((item) =>
              item.children.length > 0 ? (
                <NavDropdown key={item.key} item={item} isZh={isZh} viewAllLabel={t('nav.viewAll')} />
              ) : (
                <TopLevelLink key={item.key} item={item} isZh={isZh} />
              ),
            )}
          </nav>

          {/* Right cluster: language switcher + CTA */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Language switcher. 2026-10-18: the trigger was `px-3 py-2`
                around a `text-base` (16px) emoji — a ~36px tap target,
                under the 44px iOS/Material minimum, and optically smaller
                than the hamburger beside it. Now `min-h-11` (44px) with a
                `text-xl` (20px) flag and a matching chevron.

                2026-10-20: the `sm:` reset is GONE. It used to drop the
                trigger back to a 16px flag / 36px box at ≥640px on the
                belief that the band was desktop chrome. It isn't — the
                primary nav is `hidden xl:flex`, so below xl this cluster
                is the only navigation. Flag/chevron keep phone sizing
                through that band; the 44px tap target stays
                (`sm:py-2` around a 20px flag is 36px, so min-height wins).

                The row itself is now 88px until xl (see the responsive-
                band note on the row), so a 44px control is the same
                proportion as on phones. */}
            <div ref={langRef} className="relative">
              <button
                type="button"
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 min-h-11 rounded-yada hover:bg-surface-2 transition-colors sm:py-2"
                aria-label={t('common.languageSwitch')}
                aria-haspopup="menu"
                aria-expanded={langOpen}
                // `aria-controls` names the menu so AT can announce the trigger
                // as "expanded/collapsed, controls <name>" — required by WAI-ARIA
                // whenever a popup is described by `aria-haspopup`.
                aria-controls="navbar-lang-menu"
              >
                <span aria-hidden="true" className="text-xl leading-none">
                  {currentLang.flag}
                </span>
                <span className="hidden sm:inline text-sm font-medium text-ink-soft">
                  {currentLang.short}
                </span>
                <Icon
                  icon={langOpen ? 'lucide:chevron-up' : 'lucide:chevron-down'}
                  className="text-ink-soft text-lg"
                />
              </button>

              {langOpen && (
                <div
                  id="navbar-lang-menu"
                  role="menu"
                  // `aria-labelledby` re-points at the trigger so the menu's
                  // accessible name comes from the button's `aria-label`
                  // rather than from this anonymous div.
                  aria-labelledby={undefined}
                  className="absolute right-0 mt-2 w-44 bg-surface rounded-yada-lg shadow-yada-lg border border-border py-2 z-50"
                >
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      role="menuitemradio"
                      aria-checked={i18n.language === lang.code}
                      onClick={() => {
                        // SEO PR2 Phase 6: rewrite the URL pathname to
                        // match the chosen locale so the i18n localePath
                        // detector (Task 6.2) keeps i18n in sync on
                        // subsequent navigations / reloads. The default
                        // locale ('zh-Hant') strips the prefix; the
                        // others add /zh-Hans/ or /en/.
                        const newPath = buildLocalePath(
                          window.location.pathname,
                          lang.code as Locale,
                        )
                        window.history.replaceState(
                          null,
                          '',
                          newPath + window.location.search,
                        )
                        void i18n.changeLanguage(lang.code)
                        setLangOpen(false)
                      }}
                      className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                        i18n.language === lang.code
                          ? 'bg-surface-2 text-primary font-semibold'
                          : 'text-ink-soft hover:bg-surface-2'
                      }`}
                    >
                      <span aria-hidden="true">{lang.flag}</span>
                      <span>{lang.name}</span>
                      {i18n.language === lang.code && (
                        <Icon
                          icon="lucide:check"
                          className="ml-auto text-primary"
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA — WhatsApp. Only in the desktop chrome (xl+), where the
                primary nav is also visible. Below xl the same CTA lives
                inside the mobile drawer so the compact header stays
                logo + lang + hamburger — not a shrunk desktop bar. */}
            <a
              href="https://wa.me/85268876400"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden xl:inline-flex items-center gap-1.5 bg-primary text-surface px-4 py-2 rounded-yada-full hover:bg-primary-dark hover:text-white transition-colors text-sm font-semibold"
            >
              <Icon icon="lucide:message-circle" className="text-base" />
              <span>{t('common.bookDemo')}</span>
            </a>

            {/* Mobile menu toggle. 2026-10-18: icon `text-2xl` (24px) → 36px and
                `p-2` → `min-h-11 min-w-11`, scoped to BELOW `sm` only.
                24px read far too small beside the enlarged logo; 36px is
                the sweet spot — big enough to match the lockup's weight,
                small enough not to compete with it. The square minimum is
                the 44px tap target: `p-2` around a 24px glyph was 40px,
                which is under the iOS/Material minimum.

                2026-10-20: the `sm:` reset is GONE. Same reasoning as
                the language trigger above: `xl:hidden` means this button
                is the ONLY nav affordance from 640 to 1279px (the primary
                nav is `hidden xl:flex`), so that band is a mobile
                layout, not desktop chrome. At 1172px the hamburger was
                a 24px glyph in a 40px box — under the touch-target
                floor and visually weaker than the flag beside it.

                Now the glyph is 36px at EVERY breakpoint below `xl`, so
                the `sm:` override is simply deleted rather than retuned;
                there is no separate tablet value to maintain. The box
                is 44×44 everywhere too (dropped `sm:min-h-0`/`sm:p-2`),
                restoring the 44px tap target the reset had been
                cancelling out.

                36px was chosen over 32px by comparing both renders
                side by side at 1172px: against the 20px flag and the
                `text-sm` CTA label, 32px still read slightly timid,
                while 36px matches the mobile arm it already used and
                matches the weight of the 80px logo lockup beside it.
                The row is 110px there, so a 44px control is 40% of the
                row height — proportionate, not oversized. */}
            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="xl:hidden flex items-center justify-center min-h-11 min-w-11 rounded-yada hover:bg-surface-2"
              aria-label={mobileOpen ? t('common.closeMenu') : t('common.openMenu')}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
            >
              <Icon
                icon={mobileOpen ? 'lucide:x' : 'lucide:menu'}
                className="text-[36px] leading-none text-ink"
              />
            </button>
          </div>
        </div>
      </Container>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          id="mobile-menu"
          className="xl:hidden bg-surface border-t border-border shadow-yada-md"
          role="dialog"
          aria-modal="true"
        >
          <Container className="py-4">
            <nav aria-label="Mobile navigation">
              <ul className="flex flex-col gap-1">
                <li>
                  <NavLink
                    to="/"
                    end
                    className={({ isActive }) =>
                      `block px-4 py-3 rounded-yada ${isZh ? 'text-xl' : 'text-lg'} font-medium ${
                        isActive
                          ? 'bg-primary text-surface'
                          : 'text-ink-soft hover:bg-surface-2'
                      }`
                    }
                  >
                    {t('nav.home')}
                  </NavLink>
                </li>
                {items.map((item) => (
                  <li key={item.key}>
                    <MobileNavItem item={item} isZh={isZh} viewAllLabel={t('nav.viewAll')} />
                  </li>
                ))}
              </ul>
            </nav>
            <div className="mt-4 pt-4 border-t border-border">
              <a
                href="https://wa.me/85268876400"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-primary text-surface px-6 py-3 rounded-yada-full hover:bg-primary-dark hover:text-white transition-colors text-base font-semibold"
              >
                <Icon icon="lucide:message-circle" />
                <span>{t('common.bookDemo')}</span>
              </a>
            </div>
          </Container>
        </div>
      )}
    </header>
  )
}

/* ============================================================
   Nav item rendering
   ============================================================ */

const linkBase = (isZh: boolean) =>
  `${isZh ? 'text-lg' : 'text-base'} font-medium whitespace-nowrap transition-colors`

/** A top-level link, or an <a> when the CMS marked it external. External links
 *  keep target=_blank so the SPA is not torn down for a third-party site.
 *
 *  The href goes through `safeExternalUrl` first. React does NOT filter
 *  `javascript:` in an href — it warns and renders it anyway — so a nav item
 *  whose external URL has a script scheme would execute in every visitor's
 *  browser. The server refuses such a value on write, but this is the second
 *  lock: a row that predates the check, or one written by another path, must
 *  still render as plain text rather than as an executable link. */
function TopLevelLink({ item, isZh }: { item: ResolvedNavItem; isZh: boolean }) {
  if (item.isExternal) {
    const href = safeExternalUrl(item.href)
    if (!href) {
      return (
        <span className={`${linkBase(isZh)} text-ink-soft`} title="此連結的網址格式不正確">
          <Lines text={item.label} />
        </span>
      )
    }
    return (
      <a
        href={href}
        target={item.openNewTab ? '_blank' : undefined}
        rel={item.openNewTab ? 'noopener noreferrer' : undefined}
        className={`${linkBase(isZh)} text-ink-soft hover:text-primary`}
      >
        <Lines text={item.label} />
      </a>
    )
  }
  return (
    <NavLink
      to={item.href}
      className={({ isActive }) =>
        `${linkBase(isZh)} hover:text-primary ${isActive ? 'text-primary' : 'text-ink-soft'}`
      }
    >
      <Lines text={item.label} />
    </NavLink>
  )
}

/**
 * The CONTENTS of the desktop dropdown panel.
 *
 * WHY THIS IS A SEPARATE COMPONENT
 * --------------------------------
 * The panel only exists in the DOM while the dropdown is OPEN, and "open" is
 * a `useState` inside a component that reads the live nav snapshot. The live
 * snapshot has no children at all — every row in `cms_nav_items` is a flat
 * root — so the third level could never be seen on this site until an
 * operator built one, and there was no way to prove the renderer draws it.
 *
 * Extracting the panel makes it renderable on its own: a test can hand it a
 * tree with three levels and read the HTML back. That is what
 * `scripts/test-nav-render.mts` does.
 *
 * It is exported, not private, for exactly that reason.
 */
export function NavDropdownPanel({
  item,
  activeChild,
  panelId,
  viewAllLabel,
  onFocusBranch,
}: {
  item: ResolvedNavItem
  /** The level-1 entry whose own children fill the right-hand column. */
  activeChild: ResolvedNavNode | null
  panelId: string
  viewAllLabel: string
  onFocusBranch?: (child: ResolvedNavNode) => void
}) {
  const thirdLevel = activeChild?.children ?? []
  return (
    <div
      id={panelId}
      role="menu"
      aria-label={item.label}
      className={`absolute left-0 mt-2 ${
        thirdLevel.length > 0 ? 'w-[34rem]' : 'w-80'
      } bg-surface rounded-yada-lg shadow-yada-lg border border-border py-2 z-50`}
    >
      <div className="flex">
        <div role="group" className={thirdLevel.length > 0 ? 'w-64 shrink-0' : ''}>
          {item.children.map((child) => (
            <Link
              key={child.key}
              to={child.href}
              role="menuitem"
              onMouseEnter={() => onFocusBranch?.(child)}
              onFocus={() => onFocusBranch?.(child)}
              aria-haspopup={child.children.length > 0 ? 'menu' : undefined}
              className={`flex gap-3 px-3 py-2 hover:bg-surface-2 transition-colors ${
                child.key === activeChild?.key ? 'bg-surface-2' : ''
              }`}
            >
              {child.thumb && (
                <img
                  src={child.thumb}
                  srcSet={child.thumbSrcSet}
                  // The thumbnail is drawn in a 48px box — stating that lets
                  // the browser pick the 320px rung instead of the 1024px one.
                  sizes={child.thumbSrcSet ? '48px' : undefined}
                  alt=""
                  className="w-12 h-12 object-cover rounded-yada-sm flex-shrink-0"
                  loading="lazy"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink line-clamp-2">{child.label}</p>
              </div>
              {child.children.length > 0 && (
                <Icon
                  icon="lucide:chevron-right"
                  className="text-base opacity-60 self-center flex-shrink-0"
                />
              )}
            </Link>
          ))}
        </div>

        {thirdLevel.length > 0 && (
          <div
            role="group"
            aria-label={activeChild?.label}
            className="w-72 border-l border-surface-3 py-1 pr-1"
          >
            {thirdLevel.map((grandchild) => (
              <Link
                key={grandchild.key}
                to={grandchild.href}
                role="menuitem"
                className="block px-3 py-2 text-sm text-ink-soft hover:bg-surface-2 transition-colors"
              >
                {grandchild.label}
              </Link>
            ))}
          </div>
        )}
      </div>

      {item.collectionHref && (
        <div className="border-t border-surface-3 mt-1 pt-1">
          <Link
            to={item.collectionHref}
            role="menuitem"
            className="block px-3 py-2 text-sm font-semibold text-primary hover:bg-surface-2 transition-colors text-center"
          >
            {viewAllLabel}
          </Link>
        </div>
      )}
    </div>
  )
}

/**
 * Desktop dropdown for any top-level item that has children — a hand-authored
 * submenu, an auto-derived "latest N" list, or both.
 *
 * The panel is a TWO-COLUMN mega-panel, not a nested flyout. The left column
 * lists the item's own children; the right column lists the children of
 * whichever left-column entry is currently in focus. Nested absolutely
 * positioned flyouts were rejected deliberately: they collide with the
 * viewport edge, need a second layer of open/close state, and are unusable
 * on touch. One panel, one `role="menu"`, one click to open.
 *
 * Click-to-open matches the language switcher above rather than hover, so it
 * stays usable on touch and with a keyboard.
 */
function NavDropdown({
  item,
  isZh,
  viewAllLabel,
}: {
  item: ResolvedNavItem
  isZh: boolean
  viewAllLabel: string
}) {
  const [open, setOpen] = useState(false)
  // Which level-1 entry the right-hand column is showing. `null` means
  // "the first entry that has its own children" — the derived fallback — so
  // opening a three-level menu shows the third level straight away.
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const location = useLocation()
  // Stable id per item so aria-controls on the trigger matches the panel.
  // Using `item.key` keeps two dropdowns on the same page (mobile + desktop)
  // from colliding, and means the id survives the open/close cycle.
  const panelId = `nav-dropdown-${item.key}`

  const activeChild =
    item.children.find((c) => c.key === activeKey) ??
    item.children.find((c) => c.children.length > 0) ??
    null

  /**
   * Move the right-hand column to `child` — but only when the child HAS a
   * third level. Hovering a leaf leaves the right column alone rather than
   * blanking it, so the panel does not collapse and jump under the pointer.
   */
  const focusBranch = (child: ResolvedNavNode) => {
    if (child.children.length > 0) setActiveKey(child.key)
  }

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        className={`${linkBase(isZh)} inline-flex items-center gap-1 hover:text-primary text-ink-soft`}
      >
        <span>{item.label}</span>
        <Icon
          icon={open ? 'lucide:chevron-up' : 'lucide:chevron-down'}
          className="text-base opacity-70"
        />
      </button>

      {open && (
        <NavDropdownPanel
          item={item}
          activeChild={activeChild}
          panelId={panelId}
          viewAllLabel={viewAllLabel}
          onFocusBranch={focusBranch}
        />
      )}
    </div>
  )
}

/**
 * Mobile rendering for one menu entry, at any depth.
 *
 * Items with children become an accordion — the old hardcoded nav had no mobile
 * submenu at all, so a submenu configured in the CMS used to be unreachable on
 * a phone. That gap is why this exists rather than a flat list.
 *
 * The component is RECURSIVE, so a three-level menu nests three levels deep
 * on a phone. Each level indents a little further and uses a smaller type
 * size, so the nesting is visible at a glance.
 *
 * `initiallyExpanded` starts every level open. It exists for the render
 * test (scripts/test-nav-render.mts): the accordion's contents are behind a
 * `useState`, so without it there is no way to render the third level
 * without a browser and a live nav tree.
 */
export function MobileNavItem({
  item,
  isZh,
  viewAllLabel,
  depth = 0,
  initiallyExpanded = false,
}: {
  item: ResolvedNavItem
  isZh: boolean
  viewAllLabel: string
  depth?: number
  initiallyExpanded?: boolean
}) {
  const [expanded, setExpanded] = useState(initiallyExpanded)
  /**
   * The accordion stops at the header's own limit.
   *
   * The resolver already cuts the tree at NAV_MAX_DEPTH, so this never
   * triggers in production — but the component was recursing on whatever it
   * was handed, with no limit of its own. That is the difference between
   * "the tree is capped" and "the renderer is capped": the first is a
   * convention every caller has to remember, the second is a property of the
   * code. scripts/test-nav-render.mts hands this component a four-level tree
   * and asserts that the fourth level is not drawn.
   */
  const atMaxDepth = depth >= NAV_MAX_DEPTH
  const hasChildren = item.children.length > 0 && !atMaxDepth
  const isTop = depth === 0
  const pill = (isActive: boolean) =>
    `block px-4 py-3 rounded-yada ${
      isTop ? (isZh ? 'text-lg' : 'text-base') : 'text-sm'
    } font-medium ${
      isActive ? 'bg-primary text-surface' : 'text-ink-soft hover:bg-surface-2'
    }`

  return (
    <>
      <div className="flex items-center">
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className={`${pill(false)} flex-1 text-left flex items-center justify-between gap-2`}
          >
            <Lines text={item.label} />
            <Icon
              icon={expanded ? 'lucide:chevron-up' : 'lucide:chevron-down'}
              className="text-base opacity-70"
            />
          </button>
        ) : item.isExternal ? (
          // `safeExternalUrl` is the second lock on an admin-authored href —
          // see TopLevelLink. An unsafe value renders as text, not a link.
          safeExternalUrl(item.href) ? (
            <a
              href={safeExternalUrl(item.href)}
              target={item.openNewTab ? '_blank' : undefined}
              rel={item.openNewTab ? 'noopener noreferrer' : undefined}
              className={`${pill(false)} flex-1`}
            >
              <Lines text={item.label} />
            </a>
          ) : (
            <span className={`${pill(false)} flex-1`}>
              <Lines text={item.label} />
            </span>
          )
        ) : (
          <NavLink to={item.href} className={({ isActive }) => pill(isActive)}>
            <Lines text={item.label} />
          </NavLink>
        )}
      </div>

      {hasChildren && expanded && (
        <ul className="ml-4 border-l border-surface-3 pl-3 my-1 space-y-1">
          {item.children.map((child) => (
            <li key={child.key}>
              <MobileNavItem
                item={child}
                isZh={isZh}
                viewAllLabel={viewAllLabel}
                depth={depth + 1}
                initiallyExpanded={initiallyExpanded}
              />
            </li>
          ))}
          {item.collectionHref && (
            <li>
              <Link
                to={item.collectionHref}
                className="block px-3 py-2 rounded-yada text-sm font-semibold text-primary hover:bg-surface-2"
              >
                {viewAllLabel}
              </Link>
            </li>
          )}
        </ul>
      )}
    </>
  )
}
